<template>
  <article id="lotto-wrapper" class="contents-wrapper">
    <div id="lotto-buy-list">
      <!-- 페이지 헤더 + 요약 -->
      <div class="page-head">
        <div class="page-head-left">
          <h2 class="page-title">구매 내역</h2>
          <p class="page-sub" v-if="statsData">
            총 {{ statsData.totalGames }}게임 · 투자 {{ formatKRW(statsData.totalInvestment) }} · 회수 {{ formatKRW(statsData.totalPrize) }}
            <span
              class="roi-badge"
              :class="{ positive: statsData.roi > 0, negative: statsData.roi < 0 }"
            >{{ statsData.roi > 0 ? '+' : '' }}{{ statsData.roi.toFixed(1) }}%</span>
          </p>
        </div>
        <div class="page-head-actions" v-if="statsData">
          <button class="edit-toggle-btn" :class="{ active: editMode }" @click="toggleEditMode">
            {{ editMode ? '완료' : '편집' }}
          </button>
        </div>
      </div>

      <!-- 다음 회차 정보 + 미추첨 구매 + 통계 -->
      <div class="progress-section section-card" v-if="progressDrwNo > 0">
        <div class="progress-header">
          <div class="progress-drw">이번 회차 <span class="progress-drw-no">{{ progressDrwNo }}회차</span></div>
          <div class="progress-date">추첨일 {{ formatDrwDate(progressDrwDate) }}</div>
        </div>

        <template v-if="progressList.length > 0">
          <!-- 상단: 이번주 구매(1열) + 통계 펼침(2열) -->
          <div class="progress-layout">
            <div class="pl-left">
              <div v-for="(game, idx) in firstProgress" :key="'p-' + idx" class="game-row" :class="{ 'edit-active': editMode }">
                <input v-if="editMode" type="checkbox" class="game-checkbox" :value="game.id" v-model="selectedIds" />
                <LottoBuyGame :lottoGame="game" :editMode="editMode" @update-buy-type="onUpdateBuyType" />
              </div>
            </div>

            <div class="pl-stats stats-dashboard" v-if="statsData">
              <div class="pl-stats-title">내 통계</div>
              <div class="stats-grid">
                <div class="stats-item">
                  <div class="stats-label">총 구매</div>
                  <div class="stats-value">{{ statsData.totalGames }}게임</div>
                  <div class="stats-sub">{{ formatKRW(statsData.totalInvestment) }}</div>
                </div>
                <div class="stats-item">
                  <div class="stats-label">총 당첨금</div>
                  <div class="stats-value">{{ formatKRW(statsData.totalPrize) }}</div>
                </div>
                <div class="stats-item">
                  <div class="stats-label">수익률</div>
                  <div class="stats-value" :class="{ positive: statsData.roi > 0, negative: statsData.roi < 0 }">
                    {{ statsData.roi > 0 ? '+' : '' }}{{ statsData.roi.toFixed(1) }}%
                  </div>
                </div>
                <div class="stats-item">
                  <div class="stats-label">최고 등수</div>
                  <div class="stats-value">{{ statsData.bestRank <= 5 ? statsData.bestRank + '등' : '-' }}</div>
                </div>
              </div>
              <div class="stats-rank-dist" v-if="statsData.totalDrawnGames > 0">
                <div class="stats-rank-title">등수 분포 (추첨 완료 {{ statsData.totalDrawnGames }}게임)</div>
                <div class="stats-rank-bars">
                  <div v-for="r in [1,2,3,4,5,6]" :key="'sr-' + r" class="stats-rank-row">
                    <span class="stats-rank-label">{{ r <= 5 ? r + '등' : '낙첨' }}</span>
                    <div class="stats-rank-track">
                      <div class="stats-rank-fill" :class="'rank-fill-' + r" :style="{ width: getRankPercent(r) + '%' }"></div>
                    </div>
                    <span class="stats-rank-count">{{ statsData.rankDist[r] || 0 }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 통계 높이를 넘긴 나머지: 1·2열 모두 2열 그리드 -->
          <div class="game-grid" v-if="restProgress.length > 0">
            <div v-for="(game, idx) in restProgress" :key="'pr-' + idx" class="game-row" :class="{ 'edit-active': editMode }">
              <input v-if="editMode" type="checkbox" class="game-checkbox" :value="game.id" v-model="selectedIds" />
              <LottoBuyGame :lottoGame="game" :editMode="editMode" @update-buy-type="onUpdateBuyType" />
            </div>
          </div>
        </template>
        <div v-else class="empty-progress">이번 회차 구매 내역이 아직 없어요</div>

        <!-- 내 번호 조합 -->
        <div v-if="progressList.length > 0" class="combo-section">
          <button class="combo-toggle-btn" :class="{ active: showCombo }" @click="toggleCombo">
            내 번호 조합
          </button>

          <div v-if="showCombo" class="combo-panel">
            <div class="combo-pool">
              <div class="combo-pool-title">번호 풀 ({{ numberPool.length }}개)</div>
              <div class="combo-pool-balls">
                <div
                  v-for="num in numberPool"
                  :key="'pool-' + num"
                  class="combo-ball"
                  :class="getColorClass(num)"
                >{{ num }}</div>
              </div>
            </div>

            <div class="combo-controls">
              <select v-model="comboCount" class="combo-select">
                <option :value="5">5게임</option>
                <option :value="10">10게임</option>
                <option :value="15">15게임</option>
              </select>
              <button class="combo-generate-btn" @click="generateCombo">생성</button>
            </div>

            <div v-if="comboGames.length > 0" class="combo-results">
              <div class="combo-results-header">
                <span class="combo-results-title">조합 결과 ({{ comboGames.length }}게임)</span>
                <button class="combo-save-btn" @click="saveCombo">저장</button>
              </div>
              <div v-for="(game, idx) in comboGames" :key="'combo-' + idx" class="game-row">
                <LottoBuyGame :lottoGame="game" :hideRank="true" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 과거 회차 구매내역 -->
      <div v-if="!loading && pastGroups.length === 0 && progressList.length === 0" class="empty-state">
        <div class="empty-icon">🎟️</div>
        <div class="empty-text">아직 구매 내역이 없어요</div>
        <div class="empty-sub">번호를 만들어 저장하거나, 매장 용지의 QR을 불러와 기록해 보세요.</div>
        <button class="empty-cta" @click="goCreate">번호 생성하러 가기</button>
      </div>

      <div v-if="pastGroups.length > 0" class="past-divider">
        <span>지난 회차 구매내역</span>
      </div>

      <div v-for="round in pastGroups" :key="'r-' + round.drwNo" class="round-section section-card">
        <div class="round-header">
          <span class="round-drw">{{ round.drwNo }}회차</span>
          <span class="round-rank" :class="round.bestRankClass">{{ round.bestRankText }}</span>
        </div>
        <div v-if="winNumberMap[round.drwNo]" class="win-numbers">
          <div class="win-info">
            <span class="win-label">당첨번호</span>
            <span class="win-date">{{ winNumberMap[round.drwNo].drwDate }}</span>
          </div>
          <div class="win-balls">
            <div
              v-for="(num, i) in winNumberMap[round.drwNo].numbers"
              :key="'wn-' + round.drwNo + '-' + i"
              class="win-ball"
              :class="getColorClass(num)"
            >
              {{ num }}
            </div>
            <span class="win-plus">+</span>
            <div
              class="win-ball"
              :class="getColorClass(winNumberMap[round.drwNo].bonus)"
            >
              {{ winNumberMap[round.drwNo].bonus }}
            </div>
          </div>
        </div>
        <div v-for="(group, gIdx) in round.groups" :key="'rg-' + round.drwNo + '-' + gIdx" class="buy-group">
          <div class="buy-group-time">{{ formatBuyTime(group.time) }}</div>
          <div class="game-grid">
            <div v-for="(game, idx) in group.games" :key="'g-' + round.drwNo + '-' + gIdx + '-' + idx" class="game-row" :class="{ 'edit-active': editMode }">
              <input v-if="editMode" type="checkbox" class="game-checkbox" :value="game.id" v-model="selectedIds" />
              <LottoBuyGame :lottoGame="game" :editMode="editMode" @update-buy-type="onUpdateBuyType" />
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <div class="loading-text">불러오는 중...</div>
      </div>

      <div v-if="!loading && isLast && buyList.length > 0" class="end-state">마지막 구매내역입니다</div>

      <!-- 삭제 바 (편집 모드) -->
      <div v-if="editMode && selectedIds.length > 0" class="delete-bar">
        <span class="delete-bar-text">{{ selectedIds.length }}개 선택됨</span>
        <button class="delete-bar-btn" @click="deleteSelected">삭제</button>
      </div>
    </div>
  </article>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import { LottoGame, LottoGameBuyResponse, LottoResultResponse } from "../class";
import { createLottoGameBuyResponseNotDraw, getNowDrawNo } from "../utils/LottoUtils";
import LottoBuyGame from "../components/LottoBuyGame.vue";

interface BuyTimeGroup {
  time: string;
  games: LottoGameBuyResponse[];
}

interface RoundGroup {
  drwNo: number;
  bestRankText: string;
  bestRankClass: string;
  groups: BuyTimeGroup[];
}

interface WinNumberInfo {
  numbers: number[];
  bonus: number;
  drwDate: string;
}

export default defineComponent({
  name: "LottoBuyListView",
  components: {
    LottoBuyGame,
  },
  data() {
    return {
      buyList: [] as LottoGameBuyResponse[],
      statsGames: [] as LottoGameBuyResponse[], // 통계 집계용 전체 구매내역 (스크롤 무관)
      progressList: [] as LottoGameBuyResponse[],
      progressDrwNo: 0,
      progressDrwDate: "",
      page: 1,
      size: 20,
      totalElements: 0,
      isLast: false,
      loading: false,
      summaryLoaded: false,
      winNumberMap: {} as Record<number, WinNumberInfo>,
      editMode: false,
      selectedIds: [] as number[],
      showStats: false,
      progressColCount: 7, // 통계 카드 높이에 대략 대응 (이번주 1열에 표시할 개수)
      showCombo: false,
      comboCount: 5,
      comboGames: [] as LottoGameBuyResponse[],
    };
  },
  computed: {
    firstProgress(): LottoGameBuyResponse[] {
      return this.progressList.slice(0, this.progressColCount);
    },
    restProgress(): LottoGameBuyResponse[] {
      return this.progressList.slice(this.progressColCount);
    },
    numberPool(): number[] {
      const nums = new Set<number>();
      for (const game of this.progressList) {
        nums.add(game.drwtNo1);
        nums.add(game.drwtNo2);
        nums.add(game.drwtNo3);
        nums.add(game.drwtNo4);
        nums.add(game.drwtNo5);
        nums.add(game.drwtNo6);
      }
      return Array.from(nums).sort((a, b) => a - b);
    },
    progressGroups(): BuyTimeGroup[] {
      return this.groupByTime(this.progressList);
    },
    pastGroups(): RoundGroup[] {
      // 미추첨(rank===0) 제외, 회차별 그룹핑
      const drawn = this.buyList.filter((g) => g.isDraw());
      const roundMap = new Map<number, LottoGameBuyResponse[]>();
      for (const game of drawn) {
        if (!roundMap.has(game.drwNo)) {
          roundMap.set(game.drwNo, []);
        }
        roundMap.get(game.drwNo)!.push(game);
      }
      // 회차 내림차순
      const rounds = Array.from(roundMap.entries()).sort((a, b) => b[0] - a[0]);
      return rounds.map(([drwNo, games]) => {
        const bestRank = Math.min(...games.map((g) => g.rank));
        return {
          drwNo,
          bestRankText: this.getRankText(bestRank),
          bestRankClass: this.getRankClass(bestRank),
          groups: this.groupByTime(games),
        };
      });
    },
    statsData(): { totalGames: number; totalInvestment: number; totalPrize: number; roi: number; bestRank: number; rankDist: Record<number, number>; totalDrawnGames: number } | null {
      // 통계는 페이징된 buyList가 아니라 전체 집계본(statsGames) 기준 → 스크롤 없이 누적 정확
      const base = this.statsGames.length > 0 ? this.statsGames : this.buyList;
      const allGames = [...this.progressList, ...base];
      if (allGames.length === 0) return null;
      const totalGames = allGames.length;
      const totalInvestment = totalGames * 1000;
      const drawnGames = allGames.filter((g) => g.isDraw());
      const totalPrize = drawnGames.reduce((s, g) => s + g.prize, 0);
      const bestRank = drawnGames.length > 0 ? Math.min(...drawnGames.map((g) => g.rank)) : 99;
      const rankDist: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
      for (const g of drawnGames) {
        if (rankDist[g.rank] !== undefined) rankDist[g.rank]++;
      }
      const roi = totalInvestment > 0 ? ((totalPrize - totalInvestment) / totalInvestment) * 100 : 0;
      return { totalGames, totalInvestment, totalPrize, roi, bestRank, rankDist, totalDrawnGames: drawnGames.length };
    },
  },
  mixins: [UtilMixin],
  methods: {
    formatKRW(amount: number): string {
      if (amount >= 100000000) {
        return (amount / 100000000).toFixed(1) + "억원";
      } else if (amount >= 10000) {
        return (amount / 10000).toFixed(0) + "만원";
      }
      return amount.toLocaleString() + "원";
    },
    getRankPercent(rank: number): number {
      if (!this.statsData || this.statsData.totalDrawnGames === 0) return 0;
      return ((this.statsData.rankDist[rank] || 0) / this.statsData.totalDrawnGames) * 100;
    },
    formatBuyTime(datetime: string): string {
      if (!datetime || datetime === "알 수 없음") return datetime;
      // "2024-08-25T14:30:00" → "2024년 8월 25일 14:30"
      const str = datetime.replace("T", " ");
      const match = str.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);
      if (!match) return datetime;
      const [, y, m, d, hh, mm] = match;
      return `${y}년 ${parseInt(m)}월 ${parseInt(d)}일 ${hh}:${mm}`;
    },
    formatDrwDate(dateStr: string): string {
      if (!dateStr) return "";
      // "2024-08-31" or "2024-08-31 20:00" → "2024년 8월 31일 20:00"
      const str = dateStr.replace("T", " ");
      const match = str.match(/(\d{4})-(\d{2})-(\d{2})(\s+(\d{2}):(\d{2}))?/);
      if (!match) return dateStr;
      const [, y, m, d, , hh, mm] = match;
      let result = `${y}년 ${parseInt(m)}월 ${parseInt(d)}일`;
      if (hh && mm) result += ` ${hh}:${mm}`;
      return result;
    },
    getMinuteKey(datetime: string): string {
      if (!datetime) return "알 수 없음";
      // "2024-08-25T14:30:45" → "2024-08-25T14:30"
      const match = datetime.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
      return match ? match[1] : datetime;
    },
    groupByTime(games: LottoGameBuyResponse[]): BuyTimeGroup[] {
      const map = new Map<string, LottoGameBuyResponse[]>();
      for (const game of games) {
        const time = this.getMinuteKey(game.createDatetime);
        if (!map.has(time)) {
          map.set(time, []);
        }
        map.get(time)!.push(game);
      }
      return Array.from(map.entries())
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([time, g]) => ({ time, games: g }));
    },
    getRankText(rank: number): string {
      if (rank <= 0 || rank > 6) return "";
      if (rank === 6) return "낙첨";
      return rank + "등";
    },
    getRankClass(rank: number): string {
      switch (rank) {
        case 1:
          return "rank-1";
        case 2:
          return "rank-2";
        case 3:
          return "rank-3";
        case 4:
          return "rank-4";
        case 5:
          return "rank-5";
        case 6:
          return "rank-6";
        default:
          return "";
      }
    },
    async fetchSummary() {
      try {
        const res = await this.getApi().lotto().getLottoGameBuySummary();
        this.progressDrwNo = res.drwNo;
        this.progressDrwDate = res.drwNoDate;
        this.progressList = res.progressList;
        this.summaryLoaded = true;
      } catch (e) {
        console.error("fetchSummary error:", e);
        this.summaryLoaded = true;
      }
    },
    // 통계 집계용 전체 구매내역 (한 번에 크게 조회, 표시용 페이징과 분리)
    async fetchStatsAggregate() {
      const loginInfo = this.getCore().getLoginInfo();
      if (!loginInfo || !loginInfo.id) return;
      try {
        const res = await this.getApi().lotto().getLottoGameBuyList(loginInfo.id, 1, 100000);
        if (res && Array.isArray(res.content)) {
          this.statsGames = res.content.map((item: any) => {
            const game = new LottoGameBuyResponse();
            game.init(item);
            return game;
          });
        }
      } catch (e) {
        console.error("fetchStatsAggregate error:", e);
      }
    },
    async fetchBuyList() {
      if (this.loading || this.isLast) return;
      this.loading = true;
      try {
        const loginInfo = this.getCore().getLoginInfo();
        if (!loginInfo || !loginInfo.id) {
          this.loading = false;
          this.isLast = true;
          return;
        }
        const res = await this.getApi().lotto().getLottoGameBuyList(loginInfo.id, this.page, this.size);
        if (res && Array.isArray(res.content)) {
          const items = res.content.map((item: any) => {
            const game = new LottoGameBuyResponse();
            game.init(item);
            return game;
          });
          this.buyList = [...this.buyList, ...items];
          this.totalElements = res.totalElements || 0;
          this.isLast = res.last !== false;
          this.page++;
          // 추첨 완료된 회차의 당첨번호 조회
          const drawnDrwNos = [...new Set(items.filter((g: LottoGameBuyResponse) => g.isDraw()).map((g: LottoGameBuyResponse) => g.drwNo))];
          if (drawnDrwNos.length > 0) {
            this.fetchWinNumbers(drawnDrwNos as number[]);
          }
        } else {
          this.isLast = true;
        }
      } catch (e) {
        console.error("fetchBuyList error:", e);
      } finally {
        this.loading = false;
      }
    },
    getColorClass(number: number): string {
      if (number >= 1 && number <= 10) return "bg-yellow-400";
      if (number > 10 && number <= 20) return "bg-blue-400";
      if (number > 20 && number <= 30) return "bg-red-400";
      if (number > 30 && number <= 40) return "bg-gray-400";
      if (number > 40 && number <= 45) return "bg-green-400";
      return "bg-black-400";
    },
    async fetchWinNumbers(drwNoList: number[]) {
      const newDrwNos = drwNoList.filter((d) => !this.winNumberMap[d]);
      for (const drwNo of newDrwNos) {
        try {
          const res: LottoResultResponse = await this.getApi().lotto().getLottoResult(drwNo, false);
          if (res && res.drwNo > 0) {
            this.winNumberMap = {
              ...this.winNumberMap,
              [drwNo]: {
                numbers: [res.drwtNo1, res.drwtNo2, res.drwtNo3, res.drwtNo4, res.drwtNo5, res.drwtNo6],
                bonus: res.bnusNo,
                drwDate: this.formatDrwDate(res.drwNoDate),
              },
            };
          }
        } catch (e) {
          console.error("fetchWinNumbers error for drwNo:", drwNo, e);
        }
      }
    },
    toggleEditMode() {
      this.editMode = !this.editMode;
      if (!this.editMode) {
        this.selectedIds = [];
      }
    },
    goCreate() {
      this.getRouterUtil().moveTo("/lotto/generate");
    },
    async onUpdateBuyType(payload: { id: number; buyType: string }) {
      const { id, buyType } = payload;
      try {
        const res = await this.getApi().lotto().updateLottoGameBuyType(id, buyType);
        if (res.isSuccess()) {
          [this.progressList, this.buyList].forEach((list: any[]) =>
            list.forEach((g: any) => {
              if (g.id === id) g.buyType = buyType;
            })
          );
        } else {
          alert("유형 수정 실패\n사유 : " + res.message);
        }
      } catch (e) {
        console.error("onUpdateBuyType error:", e);
        alert("유형 수정 중 오류가 발생했습니다.");
      }
    },
    async deleteSelected() {
      if (this.selectedIds.length === 0) return;
      if (!confirm(`${this.selectedIds.length}개의 구매내역을 삭제하시겠습니까?`)) return;

      try {
        const res = await this.getApi().lotto().deleteLottoGameBuyList(this.selectedIds);
        if (res.isSuccess()) {
          alert("삭제 성공");
          this.editMode = false;
          this.selectedIds = [];
          this.buyList = [];
          this.statsGames = [];
          this.progressList = [];
          this.page = 1;
          this.isLast = false;
          this.summaryLoaded = false;
          this.winNumberMap = {};
          this.fetchSummary();
          this.fetchStatsAggregate();
          this.fetchBuyList();
        } else {
          alert("삭제 실패\n사유 : " + res.message);
        }
      } catch (e) {
        console.error("deleteSelected error:", e);
        alert("삭제 중 오류가 발생했습니다");
      }
    },
    toggleCombo() {
      this.showCombo = !this.showCombo;
      if (!this.showCombo) {
        this.comboGames = [];
      }
    },
    generateCombo() {
      const pool = this.numberPool;
      if (pool.length < 6) {
        alert("번호 풀이 6개 미만이라 조합할 수 없습니다.");
        return;
      }
      const games: LottoGameBuyResponse[] = [];
      const usedKeys = new Set<string>();
      const drwNo = getNowDrawNo();
      let attempts = 0;
      const maxAttempts = this.comboCount * 50;

      while (games.length < this.comboCount && attempts < maxAttempts) {
        attempts++;
        // pool에서 랜덤 6개 뽑기
        const shuffled = [...pool].sort(() => Math.random() - 0.5);
        const picked = shuffled.slice(0, 6).sort((a, b) => a - b);
        const key = picked.join(",");
        if (usedKeys.has(key)) continue;
        usedKeys.add(key);

        const game = new LottoGame();
        game.init(drwNo, picked[0], picked[1], picked[2], picked[3], picked[4], picked[5]);
        games.push(createLottoGameBuyResponseNotDraw(game));
      }
      this.comboGames = games;
    },
    async saveCombo() {
      if (this.comboGames.length === 0) return;
      if (!confirm(`${this.comboGames.length}게임을 저장하시겠습니까?`)) return;

      const gameList = this.comboGames.map((g) => {
        const game = new LottoGame();
        game.fromLottoGameBuyResponse(g);
        game.drwNo = getNowDrawNo();
        return game;
      });

      try {
        const res = await this.getApi().lotto().saveLottoGameBuyList(gameList);
        if (res.isSuccess()) {
          alert("저장되었습니다.");
          this.comboGames = [];
          this.showCombo = false;
          // 목록 새로고침
          this.progressList = [];
          this.buyList = [];
          this.statsGames = [];
          this.page = 1;
          this.isLast = false;
          this.summaryLoaded = false;
          this.fetchSummary();
          this.fetchStatsAggregate();
          this.fetchBuyList();
        } else {
          alert("저장 실패: " + res.message);
        }
      } catch (e) {
        console.error("saveCombo error:", e);
        alert("저장 중 오류가 발생했습니다.");
      }
    },
    handleScroll() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollTop + windowHeight >= docHeight - 200) {
        this.fetchBuyList();
      }
    },
  },
  created() {
    window.scrollTo(0, 0);
    this.fetchSummary();
    this.fetchStatsAggregate();
    this.fetchBuyList();
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
});
</script>

<style lang="scss" scoped></style>
